import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  type TableRowProps,
} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { ColoredMaterialDetails } from "components/coloredMaterialDetails/ColoredMaterialDetails";
import ColorStatus from "components/dataDisplay/ColorStatusDisplay";
import { DeltaEDisplay } from "components/dataDisplay/DeltaEDisplay";
import { NoData } from "components/dataDisplay/NoData";
import { OpacityDisplay } from "components/dataDisplay/OpacityDisplay";
import type { NamedColor } from "components/filter/color-filter/ColorFilter";
import { RestrictionsIcon } from "components/restrictions/restrictions-icon";
import { useContrastBorder } from "hooks/contrast-border";
import { useFeedbackSettings } from "hooks/feedback-settings";
import { usePreviewSpecification } from "hooks/specifications";
import type { Hierachy } from "hooks/useHierarchyData";
import {
  gradeValue,
  IndustryInternal,
  InternalApplication,
  RestrictedApplication,
  TrialApplication,
} from "hooks/utils/material-data";
import { type FC, useMemo, useState } from "react";
import { logCustomEvent } from "../../../datadog";
import { ColoredMaterialPreview } from "../preview/ColoredMaterialPreview";

export interface SearchResultsTableRowProps extends TableRowProps {
  material: ColoredMaterial;
  target?: NamedColor;
  isInternal?: boolean;
  hierachyData: Hierachy[];
  onMaterialSelect?: (material: ColoredMaterial) => void;
  activeIndustry?: string;
  activeApplicationClass?: string;
  activeApplication?: string;
}

export const SearchResultsTableRow: FC<SearchResultsTableRowProps> = ({
  material,
  target,
  isInternal,
  sx,
  hierachyData,
  onMaterialSelect,
  activeIndustry,
  activeApplicationClass,
  activeApplication,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const previewColor = usePreviewSpecification(material);
  const contrastBorder = useContrastBorder(previewColor?.value, target?.value);
  // Scoped grade — used for display labels (industry/class/application chips).
  const scopedGrade = gradeValue({
    hierachyData,
    material,
    activeIndustry,
    activeApplicationClass,
    activeApplication,
  });

  // When the scoped lookup finds nothing (e.g. EV - CQ Solutions has no grades
  // mapped yet), still show industry + application class from the active filters
  // so the chips are not blank.
  const grade =
    scopedGrade ??
    (activeIndustry
      ? {
          grade: material.material.shortGrade ?? "",
          industry: [activeIndustry],
          class: activeApplicationClass ? [activeApplicationClass] : [],
          application: activeApplication ? [activeApplication] : [],
        }
      : undefined);

  // Safety flags must never be silently hidden when the scoped lookup returns
  // undefined. Fall back to the unscoped grade so restriction/internal warnings
  // are always accurate.
  const unscopedGrade = scopedGrade ?? gradeValue({ hierachyData, material });

  const isInternalOnly = useMemo(
    () => InternalApplication.some((i) => unscopedGrade?.class.includes(i)),
    [unscopedGrade],
  );
  const isRestrcited = useMemo(
    () => RestrictedApplication.some((i) => unscopedGrade?.class.includes(i)),
    [unscopedGrade],
  );
  const isTrial = useMemo(
    () => TrialApplication.some((i) => unscopedGrade?.class.includes(i)),
    [unscopedGrade],
  );
  const isInternalIndustry = useMemo(
    () => IndustryInternal.some((i) => unscopedGrade?.industry.includes(i)),
    [unscopedGrade],
  );

  const { registerAction } = useFeedbackSettings();

  const onDetailsToggle = (value: boolean) => {
    logCustomEvent(value ? "search-details-open" : "search-details-close", {
      colored_material_experiment_id: material.eljExperimentId,
      origin: "table",
    });
    setOpen(value);
    if (value) {
      registerAction("details-open");
      onMaterialSelect?.(material);
    }
  };

  return (
    <>
      <TableRow
        {...props}
        sx={{ ...sx, "& > *": { borderBottom: "none !important" } }}
        key={material.eljExperimentId}
      >
        <TableCell align="right">
          {target && previewColor ? (
            <DeltaEDisplay preview={previewColor} target={target} />
          ) : (
            <NoData />
          )}
        </TableCell>
        <TableCell
          align="center"
          padding="none"
          sx={{ paddingTop: 1, paddingBottom: 1, lineHeight: 0 }}
        >
          <ColoredMaterialPreview
            border={contrastBorder}
            display="inline-block"
            height={48}
            width={48}
            material={material}
            target={target}
          />
        </TableCell>
        <TableCell>{material.material.publicGradeName}</TableCell>
        <TableCell>{material.color.finalColorCode ?? <NoData />}</TableCell>
        <TableCell padding="none">
          <Stack alignItems="center" justifyContent="center">
            <OpacityDisplay value={material.color.opacity} />
          </Stack>
        </TableCell>
        {isInternal && (
          <TableCell align="center">
            <Stack direction="row" spacing={1} justifyContent="center">
              <ColorStatus value={material.availability.colorStatus} />
              {isInternalIndustry ? <WarningAmberIcon color="warning" /> : ""}
              <RestrictionsIcon value={material.restrictions ?? []} />
            </Stack>
          </TableCell>
        )}
        <TableCell align="right">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => onDetailsToggle(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow sx={sx}>
        <TableCell style={{ padding: 0 }} colSpan={1000}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box bgcolor={(theme) => theme.palette.grey[100]}>
              <ColoredMaterialDetails
                material={material}
                isInternal={isInternal}
                grade={grade}
                isInternalOnly={isInternalOnly}
                isRestrcited={isRestrcited}
                isTrial={isTrial}
                isInternalIndustry={isInternalIndustry}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
